import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Pagination,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Assignment as TaskIcon,
  ArrowBack as BackIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { Task, TaskFilter } from '../../types/task';
import PageLayout from '../../components/Layout/PageLayout';
import BackButton from '../../components/BackButton';
import { useTableSort } from '../../hooks/useTableSort';
import SortableTableHead from '../../components/Table/SortableTableHead';
import { apiService } from '../../services/api';
import { dropdownService } from '../../services/dropdownService';
import { DropdownOption } from '../../types/dropdown';
import { F_TASKS_HK } from "../../types/stage2";
import {
  getMixingPlantLocationFullName,
  getConcreteSupplierFullName,
  getConcreteSupplierFullNameWithoutCode,
  getMixingPlantLocationFullNameWithoutCode,
  getCementTypeFullName,
  getCementSourceFullName,
  getPfaSourceFullName,
  getGgbsSourceFullName,
  getAggregateSourceFullName,
  getAdmixtureSourceFullName
} from '../../utils/dropdownUtils';
import { useTranslation } from '../../hooks/useTranslation';

// XML解析工具函數
const parseXMLData = (xmlString: string): any => {
  try {
    if (!xmlString || xmlString.trim() === '') {
      return null;
    }

    // 創建XML解析器
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

    // 檢查解析錯誤
    const parseError = xmlDoc.querySelector('parsererror');
    if (parseError) {
      console.error('XML解析錯誤:', parseError.textContent);
      return null;
    }

    // 查找MIX_ID節點
    const mixIdNode = xmlDoc.querySelector('MIX_ID');
    if (!mixIdNode) {
      console.warn('未找到MIX_ID節點');
      return null;
    }

    // 提取所有子節點的數據
    const data: any = {};
    const children = mixIdNode.children;

    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      const tagName = child.tagName;
      const textContent = child.textContent || '';

      // 只提取有值的節點
      if (textContent.trim() !== '') {
        data[tagName] = textContent.trim();
      }
    }

    return data;
  } catch (error) {
    console.error('XML解析失敗:', error);
    return null;
  }
};

// XML生成工具函數
const generateXMLFromData = (data: any): string => {
  try {
    let xmlString = '<?xml version=\"1.0\" standalone=\"yes\"?><DocumentElement><MIX_ID>';

    // 按順序添加所有字段
    const fieldOrder = [
      'MIX_ID', 'CEDD_MIX_ID', 'GRADE_TEXT', 'DESIGNED_SLUMP', 'DESIGNED_FLOW_VALUE',
      'DESIGNED_SLUMP_FLOW', 'T_500', 'T_END_OF_FLOW', 'USAGE', 'CEMENT_CONTENT', 'CEMENT_TYPE', "CEMENT_TYPE_TEXT",
      'CEMENT_SOURCE', "CEMENT_SOURCE_TEXT", 'PFA_CONTENT', 'PFA_TYPE', 'PFA_TYPE_TEXT', 'PFA_SOURCE', "PFA_SOURCE_TEXT",
      'GGBS_CONTENT', 'GGBS_TYPE', 'GGBS_TYPE_TEXT', 'GGBS_SOURCE', "GGBS_SOURCE_TEXT", 'AGG_40_CONTENT', 'AGG_40_TYPE', "AGG_40_TYPE_TEXT",
      'AGG_40_SOURCE', 'AGG_40_SOURCE_TEXT', 'AGG_20_CONTENT', 'AGG_20_TYPE', 'AGG_20_TYPE_TEXT', 'AGG_20_SOURCE', "AGG_20_SOURCE_TEXT",
      'AGG_10_CONTENT', 'AGG_10_TYPE', 'AGG_10_TYPE_TEXT', 'AGG_10_SOURCE', "AGG_10_SOURCE_TEXT", 'AGG_FINE_CONTENT',
      'AGG_FINE_TYPE', 'AGG_FINE_TYPE_TEXT', 'AGG_FINE_SOURCE', "AGG_FINE_SOURCE_TEXT", 'AGG_RS_CONTENT', 'AGG_RS_TYPE', 'AGG_RS_TYPE_TEXT',
      'AGG_RS_SOURCE', 'AGG_RS_SOURCE_TEXT', 'ADMIXTURE1_CONTENT', 'ADMIXTURE1_TYPE', 'ADMIXTURE1_TYPE_TEXT', 'ADMIXTURE1_SOURCE', 'ADMIXTURE1_SOURCE_TEXT',
      'ADMIXTURE2_CONTENT', 'ADMIXTURE2_TYPE', 'ADMIXTURE2_TYPE_TEXT', 'ADMIXTURE2_SOURCE', 'ADMIXTURE2_SOURCE_TEXT',
      'ADMIXTURE3_CONTENT', 'ADMIXTURE3_TYPE', 'ADMIXTURE3_TYPE_TEXT', 'ADMIXTURE3_SOURCE', 'ADMIXTURE3_SOURCE_TEXT',
      'ADMIXTURE4_CONTENT', 'ADMIXTURE4_TYPE', 'ADMIXTURE4_TYPE_TEXT', 'ADMIXTURE4_SOURCE', 'ADMIXTURE4_SOURCE_TEXT',
      'ADMIXTURE5_CONTENT', 'ADMIXTURE5_TYPE', 'ADMIXTURE5_TYPE_TEXT', 'ADMIXTURE5_SOURCE', 'ADMIXTURE5_SOURCE_TEXT',
      'CONCRETE_SUPPLIER', 'CONCRETE_SUPPLIER_CODE', 'MIXING_PLANT_LOCATION', 'MIX_PLANT_CODE',
      'PFA_PCT', 'FRESH_WATER_VOL', 'RECYC_WATER_VOL', 'AC_RATIO', 'WC_RATIO', 'AAR_VALUE', 'ADMIXTURE_UNIT', 'DISPLAY_UNIT'
    ];

    fieldOrder.forEach(field => {
      if (data[field] == undefined || data[field] == null && data[field] == '') {
        xmlString += `<${field}/>`;
      } else {
        // if (data[field] !== undefined && data[field] !== null && data[field] !== '') {
        xmlString += `<${field}>${data[field]}</${field}>`;
      }
    });

    xmlString += '</MIX_ID></DocumentElement>';
    return xmlString;
  } catch (error) {
    console.error('XML生成失敗:', error);
    return '';
  }
};

const TaskPage: React.FC = () => {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState<F_TASKS_HK[]>([]);
  const [loading, setLoading] = useState(false);

  // 表格排序功能
  const getValue = (item: F_TASKS_HK, key: string) => {
    switch (key) {
      case 'taskId':
        return item.TASK_ID;
      case 'mixID':
        return parseXMLData(item.ARG1_VALUE || '')?.MIX_ID || '';
      case 'grade':
        return parseXMLData(item.ARG1_VALUE || '')?.GRADE_TEXT || '';
      case 'designedSlump':
        return parseXMLData(item.ARG1_VALUE || '')?.DESIGNED_SLUMP || '';
      case 'concreteSupplier':
        return parseXMLData(item.ARG1_VALUE || '')?.CONCRETE_SUPPLIER || '';
      case 'mixingPlantLocation':
        return parseXMLData(item.ARG1_VALUE || '')?.MIXING_PLANT_LOCATION || '';
      case 'createdDate':
        return new Date(item.CREATED_DATE || '');
      case 'status':
        return item.STATUS;
      default:
        return '';
    }
  };

  const { sortConfig, handleSort, sortedData } = useTableSort(tasks, getValue);
  const [selectedTask, setSelectedTask] = useState<F_TASKS_HK | null>(null);
  const [openTaskDialog, setOpenTaskDialog] = useState(false);
  const [openApprovalDialog, setOpenApprovalDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedTaskDetails, setSelectedTaskDetails] = useState<F_TASKS_HK | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [searchTerm, setSearchTerm] = useState('');

  // 確認對話框狀態
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    action: '',
    task: null as F_TASKS_HK | null
  });

  // 下拉選單數據狀態
  const [concreteSuppliers, setConcreteSuppliers] = useState<DropdownOption[]>([]);
  const [mixingPlantLocations, setMixingPlantLocations] = useState<DropdownOption[]>([]);
  const [cementType, setCementType] = useState<DropdownOption[]>([]);
  const [cementSources, setCementSources] = useState<DropdownOption[]>([]);
  const [pfaSources, setPfaSources] = useState<DropdownOption[]>([]);
  const [ggbsSources, setGGBSSources] = useState<DropdownOption[]>([]);
  const [aggregateSources, setAggregateSources] = useState<DropdownOption[]>([]);
  const [admixtureSources, setAdmixtureSources] = useState<DropdownOption[]>([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  // 分頁狀態
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  // 篩選狀態
  const [filters, setFilters] = useState<TaskFilter>({});
  const [selectedTaskType, setSelectedTaskType] = useState('MIX_ID_EDIT');

  useEffect(() => {
    fetchTasks();
    loadDropdownData();
  }, [currentPage, selectedTaskType]);

  const loadDropdownData = async () => {
    try {
      setDropdownLoading(true);
      const [suppliers, plants, sources] = await Promise.all([
        dropdownService.getConcreteSuppliers(),
        dropdownService.getMixingPlantLocations(),
        dropdownService.getMaterialSources()
      ]);

      setConcreteSuppliers(suppliers);
      setMixingPlantLocations(plants);
      setCementType(sources.cementType);
      setCementSources(sources.cementSources);
      setPfaSources(sources.pfaSources);
      setGGBSSources(sources.ggbsSources);
      setAggregateSources(sources.aggregateSources);
      setAdmixtureSources(sources.admixtureSources);

    } catch (error) {
      console.error('載入下拉選單數據失敗:', error);
      showSnackbar('載入下拉選單數據失敗', 'error');
    } finally {
      setDropdownLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      setLoading(true);

      const response = await apiService.getTasks({
        web_subroutines_name: selectedTaskType,
        page: currentPage,
        pageSize: pageSize,
      });

      setTasks(response.tasks);
      setTotalCount(response.totalCount);

    } catch (error) {
      showSnackbar('獲取任務數據失敗', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = (task: F_TASKS_HK) => {
    setConfirmDialog({
      open: true,
      title: t('pages.mixID.confirmApprove.title'),
      message: t('pages.mixID.confirmApprove.message').replace('{taskId}', task.TASK_ID.toString()),
      action: 'approve',
      task: task
    });
  };

  const handleConfirmApprove = async () => {
    if (!confirmDialog.task) return;

    try {
      await apiService.approveTaskAction(confirmDialog.task.TASK_ID);
      showSnackbar(t('pages.mixID.messages.approveSuccess'), 'success');
      fetchTasks();
    } catch (error) {
      console.error('審批失敗:', error);
      showSnackbar(t('pages.mixID.messages.approveFailed'), 'error');
    } finally {
      setConfirmDialog({ open: false, title: '', message: '', action: '', task: null });
    }
  };

  const handleReject = (task: F_TASKS_HK) => {
    setConfirmDialog({
      open: true,
      title: t('pages.mixID.confirmReject.title'),
      message: t('pages.mixID.confirmReject.message').replace('{taskId}', task.TASK_ID.toString()),
      action: 'reject',
      task: task
    });
  };

  const handleConfirmReject = async () => {
    if (!confirmDialog.task) return;

    try {
      await apiService.rejectTaskAction(confirmDialog.task.TASK_ID);
      showSnackbar(t('pages.mixID.messages.rejectSuccess'), 'success');
      fetchTasks();
    } catch (error) {
      console.error('拒絕失敗:', error);
      showSnackbar(t('pages.mixID.messages.rejectFailed'), 'error');
    } finally {
      setConfirmDialog({ open: false, title: '', message: '', action: '', task: null });
    }
  };

  const handleEdit = (task: F_TASKS_HK) => {
    setSelectedTask(task);
    setOpenTaskDialog(true);
  };

  const handleMixIdClick = (task: F_TASKS_HK) => {
    setSelectedTaskDetails(task);
    setOpenDetailsDialog(true);
  };

  const handleSaveTask = async () => {
    if (!selectedTask) return;

    try {
      // 調用API更新任務
      await apiService.createEditEntry({
        taskId: selectedTask.TASK_ID.toString(),
        action: 'edit',
        remarks: '任務已編輯',
        updatedChanges: {
          ARG1_VALUE: selectedTask.ARG1_VALUE
        }
      });

      showSnackbar('任務已成功更新', 'success');
      setOpenTaskDialog(false);
      fetchTasks(); // 刷新任務列表
    } catch (error) {
      console.error('保存任務失敗:', error);
      showSnackbar('保存任務失敗', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pending': return 'warning';
      case 'Approved': return 'success';
      case 'Rejected': return 'error';
      case 'In Progress': return 'info';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'error';
      case 'Medium': return 'warning';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  return (
    <PageLayout title="LIMS Tasks">
      <Container maxWidth="xl">
        {/* 任務選擇 */}
        <Paper sx={{ p: 3, mb: 3, backgroundColor: 'grey.50' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                {t('pages.mixID.actions.task')}
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={selectedTaskType}
                  onChange={(e) => setSelectedTaskType(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="MIX_ID_EDIT">{t('pages.mixID.actions.editMixIDEntry')}</MenuItem>
                  <MenuItem value="MIX_ID_CREATION">{t('pages.mixID.actions.createMixIDEntry')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
              <Button
                variant="outlined"
                startIcon={<BackIcon />}
                onClick={() => window.history.back()}
                sx={{ mr: 2 }}
              >
                {t('common.back')}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* 任務列表 */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table>
                <SortableTableHead
                  columns={[
                    { key: 'taskId', label: 'Task ID' },
                    { key: 'status', label: t('common.status') },
                    { key: 'mixID', label: t('common.item') },
                    { key: 'action', label: t('common.action'), sortable: false }
                  ]}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <TableBody>
                  {sortedData.map((task, index) => (
                    <TableRow
                      key={task.TASK_ID}
                      sx={{
                        backgroundColor: index % 2 === 0 ? 'background.paper' : 'grey.50',
                        '&:hover': { backgroundColor: 'action.hover' }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TaskIcon sx={{ mr: 1, color: 'primary.main' }} />
                          {task.TASK_ID}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={t('common.' + task.STATUS)}
                          color={getStatusColor(task.STATUS || '')}
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleMixIdClick(task)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 'bold',
                            borderColor: '#1976d2',
                            color: '#1976d2',
                            '&:hover': {
                              backgroundColor: '#e3f2fd',
                              borderColor: '#1565c0'
                            }
                          }}
                        >
                          {"Mix ID: " + (parseXMLData(task.ARG1_VALUE || '')?.MIX_ID || 'N/A')}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {/* <Tooltip title="查看詳情">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleView(task)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip> */}
                          <Tooltip title={t('common.edit')}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEdit(task)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('common.approve')}>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleApprove(task)}
                            >
                              <ApproveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('common.reject')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleReject(task)}
                            >
                              <RejectIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* 分頁 */}
            {totalCount > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={Math.ceil(totalCount / pageSize)}
                  page={currentPage}
                  onChange={(event, page) => setCurrentPage(page)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}

        {/* 編輯任務對話框 */}
        <Dialog
          open={openTaskDialog}
          onClose={() => setOpenTaskDialog(false)}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: { minHeight: '80vh' }
          }}
        >
          <DialogTitle sx={{ pb: 2 }}>
            <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
              {t('pages.rfidManagement.actions.edit')} - {selectedTask?.TASK_ID}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ px: 3 }}>
            {selectedTask && (
              <Box sx={{ mt: 2 }}>
                {(() => {
                  const parsedData = parseXMLData(selectedTask.ARG1_VALUE || '');

                  if (parsedData) {
                    return (
                      <Box>
                        {/* 頂部區域 - 供應商和廠房 */}
                        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                                {t('pages.mixID.form.concreteSupplier')}:
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <FormControl fullWidth>
                                <Select
                                  value={parsedData.CONCRETE_SUPPLIER_CODE}
                                  onChange={(e) => {
                                    const updatedData = { ...parsedData, CONCRETE_SUPPLIER_CODE: e.target.value, CONCRETE_SUPPLIER: getConcreteSupplierFullNameWithoutCode(e.target.value, concreteSuppliers) };
                                    const xmlString = generateXMLFromData(updatedData);
                                    setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                  }}
                                  disabled={dropdownLoading}
                                >
                                  {concreteSuppliers.map((supplier) => (
                                    <MenuItem key={supplier.id} value={supplier.LIST_KEY}>
                                      {`${supplier.LIST_NAME} - ${supplier.LIST_DESCRIPTION}` || supplier.LIST_KEY}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                                {t('pages.mixID.form.mixingPlantLocation')}:
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <FormControl fullWidth>
                                <Select
                                  value={parsedData.MIX_PLANT_CODE}
                                  onChange={(e) => {
                                    const updatedData = { ...parsedData, MIX_PLANT_CODE: e.target.value, MIXING_PLANT_LOCATION: getMixingPlantLocationFullNameWithoutCode(e.target.value, mixingPlantLocations) };
                                    const xmlString = generateXMLFromData(updatedData);
                                    setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                  }}
                                  disabled={dropdownLoading}
                                >
                                  {mixingPlantLocations.map((plant) => (
                                    <MenuItem key={plant.id} value={plant.LIST_KEY}>
                                      {`${plant.LIST_KEY} - ${plant.LIST_DESCRIPTION}` || plant.LIST_KEY}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Paper>

                        {/* Mix ID 基本資訊 */}
                        <Paper sx={{ p: 2, mb: 3 }}>
                          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                            {t('pages.mixID.mixIDInfo')}
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                                Mix ID:
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <TextField
                                fullWidth
                                value={parsedData.MIX_ID || ''}
                                onChange={(e) => {
                                  const updatedData = { ...parsedData, MIX_ID: e.target.value };
                                  const xmlString = generateXMLFromData(updatedData);
                                  setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                }}
                                disabled
                                variant="outlined"
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                                CEDD Mix ID:
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <TextField
                                fullWidth
                                value={parsedData.CEDD_MIX_ID || ''}
                                onChange={(e) => {
                                  const updatedData = { ...parsedData, CEDD_MIX_ID: e.target.value };
                                  const xmlString = generateXMLFromData(updatedData);
                                  setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                }}
                                variant="outlined"
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                                {t('pages.mixID.table.grade')}:
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <TextField
                                fullWidth
                                value={parsedData.GRADE_TEXT || ''}
                                onChange={(e) => {
                                  const updatedData = { ...parsedData, GRADE_TEXT: e.target.value };
                                  const xmlString = generateXMLFromData(updatedData);
                                  setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                }}
                                variant="outlined"
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                                Slump:
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <TextField
                                fullWidth
                                value={parsedData.DESIGNED_SLUMP || ''}
                                onChange={(e) => {
                                  const updatedData = { ...parsedData, DESIGNED_SLUMP: e.target.value };
                                  const xmlString = generateXMLFromData(updatedData);
                                  setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                }}
                                variant="outlined"
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                                Flow Value:
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <TextField
                                fullWidth
                                value={parsedData.DESIGNED_FLOW_VALUE || ''}
                                onChange={(e) => {
                                  const updatedData = { ...parsedData, DESIGNED_FLOW_VALUE: e.target.value };
                                  const xmlString = generateXMLFromData(updatedData);
                                  setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                }}
                                variant="outlined"
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                                Slump Flow:
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <TextField
                                fullWidth
                                value={parsedData.DESIGNED_SLUMP_FLOW || ''}
                                onChange={(e) => {
                                  const updatedData = { ...parsedData, DESIGNED_SLUMP_FLOW: e.target.value };
                                  const xmlString = generateXMLFromData(updatedData);
                                  setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                }}
                                variant="outlined"
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                                Usage:
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <TextField
                                fullWidth
                                value={parsedData.USAGE || ''}
                                onChange={(e) => {
                                  const updatedData = { ...parsedData, USAGE: e.target.value };
                                  const xmlString = generateXMLFromData(updatedData);
                                  setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                }}
                                variant="outlined"
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                                T500 (sec):
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <TextField
                                fullWidth
                                value={parsedData.T_500 || ''}
                                onChange={(e) => {
                                  const updatedData = { ...parsedData, T_500: e.target.value };
                                  const xmlString = generateXMLFromData(updatedData);
                                  setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                }}
                                variant="outlined"
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                                TEnd-of-flow (sec):
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <TextField
                                fullWidth
                                value={parsedData.T_END_OF_FLOW || ''}
                                onChange={(e) => {
                                  const updatedData = { ...parsedData, T_END_OF_FLOW: e.target.value };
                                  const xmlString = generateXMLFromData(updatedData);
                                  setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                }}
                                variant="outlined"
                              />
                            </Grid>
                          </Grid>
                        </Paper>

                        {/* 配比內容表格 */}
                        <Paper sx={{ p: 2, mb: 3 }}>
                          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                            {t('pages.mixID.form.mixContent')}
                          </Typography>
                          <TableContainer>
                            <Table size="small" sx={{ tableLayout: 'fixed' }}>
                              <TableHead>
                                <TableRow sx={{ backgroundColor: 'primary.light' }}>
                                  <TableCell sx={{ fontWeight: 'bold', width: '35%', textAlign: 'left' }}>{t('pages.mixID.form.mixContent')}</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold', width: '32%', textAlign: 'center' }}>{t('pages.mixID.form.type')}</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold', width: '33%', textAlign: 'center' }}>{t('pages.mixID.form.source')}</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {/* 水泥 */}
                                <TableRow>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                          <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                            Cement:
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={8}>
                                          <TextField
                                            size="small"
                                            value={parsedData.CEMENT_CONTENT || ''}
                                            onChange={(e) => {
                                              const updatedData = { ...parsedData, CEMENT_CONTENT: e.target.value };
                                              const xmlString = generateXMLFromData(updatedData);
                                              setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                            }}
                                            InputProps={{ endAdornment: 'kg/m³' }}
                                            sx={{ width: '100%' }}
                                          />
                                        </Grid>
                                      </Grid>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <FormControl size="small" fullWidth>
                                      <Select
                                        value={parsedData.CEMENT_TYPE || ''}
                                        onChange={(e) => {
                                          const updatedData = { ...parsedData, CEMENT_TYPE: e.target.value };
                                          const xmlString = generateXMLFromData(updatedData);
                                          setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                        }}
                                      >
                                        {cementType.map((type) => (
                                          <MenuItem key={type.id} value={type.LIST_KEY}>
                                            {`${type.LIST_KEY} - ${type.LIST_DESCRIPTION}` || type.LIST_KEY}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <FormControl size="small" fullWidth>
                                      <Select
                                        value={parsedData.CEMENT_SOURCE || ''}
                                        onChange={(e) => {
                                          const updatedData = { ...parsedData, CEMENT_SOURCE: e.target.value, CEMENT_SOURCE_TEXT: getCementSourceFullName(e.target.value, cementSources) };
                                          const xmlString = generateXMLFromData(updatedData);
                                          setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                        }}
                                      >
                                        {cementSources.map((cement) => (
                                          <MenuItem key={cement.id} value={cement.LIST_KEY}>
                                            {`${cement.LIST_KEY} - ${cement.LIST_DESCRIPTION}` || cement.LIST_KEY}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </TableCell>
                                </TableRow>

                                {/* PFA */}
                                <TableRow>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                          <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                            PFA:
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={8}>
                                          <TextField
                                            size="small"
                                            value={parsedData.PFA_CONTENT || ''}
                                            onChange={(e) => {
                                              const updatedData = { ...parsedData, PFA_CONTENT: e.target.value };
                                              const xmlString = generateXMLFromData(updatedData);
                                              setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                            }}
                                            InputProps={{ endAdornment: 'kg/m³' }}
                                            sx={{ width: '100%' }}
                                          />
                                        </Grid>
                                      </Grid>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <TextField
                                      size="small"
                                      value={parsedData.PFA_TYPE || ''}
                                      onChange={(e) => {
                                        const updatedData = { ...parsedData, PFA_TYPE: e.target.value };
                                        const xmlString = generateXMLFromData(updatedData);
                                        setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                      }}
                                      sx={{ width: '100%' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <FormControl size="small" fullWidth>
                                      <Select
                                        value={parsedData.PFA_SOURCE || ''}
                                        onChange={(e) => {
                                          const updatedData = { ...parsedData, PFA_SOURCE: e.target.value, PFA_SOURCE_TEXT: getPfaSourceFullName(e.target.value, pfaSources) };
                                          const xmlString = generateXMLFromData(updatedData);
                                          setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                        }}
                                        disabled={dropdownLoading}
                                      >
                                        {pfaSources.map((pfa) => (
                                          <MenuItem key={pfa.id} value={pfa.LIST_KEY}>
                                            {`${pfa.LIST_KEY} - ${pfa.LIST_DESCRIPTION}` || pfa.LIST_KEY}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </TableCell>
                                </TableRow>

                                {/* GGBS */}
                                <TableRow>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                          <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                            GGBS:
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={8}>
                                          <TextField
                                            size="small"
                                            value={parsedData.GGBS_CONTENT || ''}
                                            onChange={(e) => {
                                              const updatedData = { ...parsedData, GGBS_CONTENT: e.target.value };
                                              const xmlString = generateXMLFromData(updatedData);
                                              setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                            }}
                                            InputProps={{ endAdornment: 'kg/m³' }}
                                            sx={{ width: '100%' }}
                                          />
                                        </Grid>
                                      </Grid>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <TextField
                                      size="small"
                                      value={parsedData.GGBS_TYPE || ''}
                                      onChange={(e) => {
                                        const updatedData = { ...parsedData, GGBS_TYPE: e.target.value };
                                        const xmlString = generateXMLFromData(updatedData);
                                        setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                      }}
                                      sx={{ width: '100%' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <FormControl size="small" fullWidth>
                                      <Select
                                        value={parsedData.GGBS_SOURCE || ''}
                                        onChange={(e) => {
                                          const updatedData = { ...parsedData, GGBS_SOURCE: e.target.value, GGBS_SOURCE_TEXT: getGgbsSourceFullName(e.target.value, ggbsSources) };
                                          const xmlString = generateXMLFromData(updatedData);
                                          setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                        }}
                                      >
                                        {ggbsSources.map((ggbs) => (
                                          <MenuItem key={ggbs.id} value={ggbs.LIST_KEY}>
                                            {`${ggbs.LIST_KEY} - ${ggbs.LIST_DESCRIPTION}` || ggbs.LIST_KEY}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </TableCell>
                                </TableRow>

                                {/* 骨料 (40mm) */}
                                <TableRow>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                          <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                            Aggregate (40mm):
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={8}>
                                          <TextField
                                            size="small"
                                            value={parsedData.AGG_40_CONTENT || ''}
                                            onChange={(e) => {
                                              const updatedData = { ...parsedData, AGG_40_CONTENT: e.target.value };
                                              const xmlString = generateXMLFromData(updatedData);
                                              setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                            }}
                                            InputProps={{ endAdornment: 'kg/m³' }}
                                            sx={{ width: '100%' }}
                                          />
                                        </Grid>
                                      </Grid>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <TextField
                                      size="small"
                                      value={parsedData.AGG_40_TYPE || ''}
                                      onChange={(e) => {
                                        const updatedData = { ...parsedData, AGG_40_TYPE: e.target.value };
                                        const xmlString = generateXMLFromData(updatedData);
                                        setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                      }}
                                      sx={{ width: '100%' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <FormControl size="small" fullWidth>
                                      <Select
                                        value={parsedData.AGG_40_SOURCE || ''}
                                        onChange={(e) => {
                                          const updatedData = { ...parsedData, AGG_40_SOURCE: e.target.value, AGG_40_SOURCE_TEXT: getAggregateSourceFullName(e.target.value, aggregateSources) };
                                          const xmlString = generateXMLFromData(updatedData);
                                          setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                        }}
                                      >
                                        {aggregateSources.map((agg) => (
                                          <MenuItem key={agg.id} value={agg.LIST_KEY}>
                                            {`${agg.LIST_KEY} - ${agg.LIST_DESCRIPTION}` || agg.LIST_KEY}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </TableCell>
                                </TableRow>

                                {/* 骨料 (20mm) */}
                                <TableRow>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                          <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                            Aggregate (20mm):
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={8}>
                                          <TextField
                                            size="small"
                                            value={parsedData.AGG_20_CONTENT || ''}
                                            onChange={(e) => {
                                              const updatedData = { ...parsedData, AGG_20_CONTENT: e.target.value };
                                              const xmlString = generateXMLFromData(updatedData);
                                              setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                            }}
                                            InputProps={{ endAdornment: 'kg/m³' }}
                                            sx={{ width: '100%' }}
                                          />
                                        </Grid>
                                      </Grid>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <TextField
                                      size="small"
                                      value={parsedData.AGG_20_TYPE || ''}
                                      onChange={(e) => {
                                        const updatedData = { ...parsedData, AGG_20_TYPE: e.target.value };
                                        const xmlString = generateXMLFromData(updatedData);
                                        setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                      }}
                                      sx={{ width: '100%' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <FormControl size="small" fullWidth>
                                      <Select
                                        value={parsedData.AGG_20_SOURCE || ''}
                                        onChange={(e) => {
                                          const updatedData = { ...parsedData, AGG_20_SOURCE: e.target.value, AGG_20_SOURCE_TEXT: getAggregateSourceFullName(e.target.value, aggregateSources) };
                                          const xmlString = generateXMLFromData(updatedData);
                                          setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                        }}
                                      >
                                        {aggregateSources.map((agg) => (
                                          <MenuItem key={agg.id} value={agg.LIST_KEY}>
                                            {`${agg.LIST_KEY} - ${agg.LIST_DESCRIPTION}` || agg.LIST_KEY}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </TableCell>
                                </TableRow>

                                {/* 骨料 (10mm) */}
                                <TableRow>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                          <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                            Aggregate (10mm):
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={8}>
                                          <TextField
                                            size="small"
                                            value={parsedData.AGG_10_CONTENT || ''}
                                            onChange={(e) => {
                                              const updatedData = { ...parsedData, AGG_10_CONTENT: e.target.value };
                                              const xmlString = generateXMLFromData(updatedData);
                                              setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                            }}
                                            InputProps={{ endAdornment: 'kg/m³' }}
                                            sx={{ width: '100%' }}
                                          />
                                        </Grid>
                                      </Grid>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <TextField
                                      size="small"
                                      value={parsedData.AGG_10_TYPE || ''}
                                      onChange={(e) => {
                                        const updatedData = { ...parsedData, AGG_10_TYPE: e.target.value };
                                        const xmlString = generateXMLFromData(updatedData);
                                        setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                      }}
                                      sx={{ width: '100%' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <FormControl size="small" fullWidth>
                                      <Select
                                        value={parsedData.AGG_10_SOURCE || ''}
                                        onChange={(e) => {
                                          const updatedData = { ...parsedData, AGG_10_SOURCE: e.target.value, AGG_10_SOURCE_TEXT: getAggregateSourceFullName(e.target.value, aggregateSources) };
                                          const xmlString = generateXMLFromData(updatedData);
                                          setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                        }}
                                      >
                                        {aggregateSources.map((agg) => (
                                          <MenuItem key={agg.id} value={agg.LIST_KEY}>
                                            {`${agg.LIST_KEY} - ${agg.LIST_DESCRIPTION}` || agg.LIST_KEY}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </TableCell>
                                </TableRow>

                                {/* 細骨料 */}
                                <TableRow>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                          <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                            Fine Aggregate:
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={8}>
                                          <TextField
                                            size="small"
                                            value={parsedData.AGG_FINE_CONTENT || ''}
                                            onChange={(e) => {
                                              const updatedData = { ...parsedData, AGG_FINE_CONTENT: e.target.value };
                                              const xmlString = generateXMLFromData(updatedData);
                                              setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                            }}
                                            InputProps={{ endAdornment: 'kg/m³' }}
                                            sx={{ width: '100%' }}
                                          />
                                        </Grid>
                                      </Grid>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <TextField
                                      size="small"
                                      value={parsedData.AGG_FINE_TYPE || ''}
                                      onChange={(e) => {
                                        const updatedData = { ...parsedData, AGG_FINE_TYPE: e.target.value };
                                        const xmlString = generateXMLFromData(updatedData);
                                        setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                      }}
                                      sx={{ width: '100%' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <FormControl size="small" fullWidth>
                                      <Select
                                        value={parsedData.AGG_FINE_SOURCE || ''}
                                        onChange={(e) => {
                                          const updatedData = { ...parsedData, AGG_FINE_SOURCE: e.target.value , AGG_FINE_SOURCE_TEXT: getAggregateSourceFullName(e.target.value, aggregateSources) };
                                          const xmlString = generateXMLFromData(updatedData);
                                          setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                        }}
                                      >
                                        {aggregateSources.map((agg) => (
                                          <MenuItem key={agg.id} value={agg.LIST_KEY}>
                                            {`${agg.LIST_KEY} - ${agg.LIST_DESCRIPTION}` || agg.LIST_KEY}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </TableCell>
                                </TableRow>

                                {/* 外加劑 1 */}
                                <TableRow>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                          <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                            Admixture 1:
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={8}>
                                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <TextField
                                              size="small"
                                              value={parsedData.ADMIXTURE1_CONTENT || ''}
                                              onChange={(e) => {
                                                const updatedData = { ...parsedData, ADMIXTURE1_CONTENT: e.target.value };
                                                const xmlString = generateXMLFromData(updatedData);
                                                setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                              }}
                                              sx={{ flex: 1 }}
                                            />
                                            <FormControl size="small" sx={{ minWidth: 80 }}>
                                              <Select
                                                value={parsedData.ADMIXTURE_UNIT || 'L_P_M3'}
                                                onChange={(e) => {
                                                  const updatedData = { ...parsedData, ADMIXTURE_UNIT: e.target.value };
                                                  const xmlString = generateXMLFromData(updatedData);
                                                  setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                                }}
                                              >
                                                <MenuItem value="L_P_M3">l/m³</MenuItem>
                                                <MenuItem value="KG_P_M3">kg/m³</MenuItem>
                                              </Select>
                                            </FormControl>
                                          </Box>
                                        </Grid>
                                      </Grid>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <TextField
                                      size="small"
                                      value={parsedData.ADMIXTURE1_TYPE || ''}
                                      onChange={(e) => {
                                        const updatedData = { ...parsedData, ADMIXTURE1_TYPE: e.target.value };
                                        const xmlString = generateXMLFromData(updatedData);
                                        setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                      }}
                                      sx={{ width: '100%' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <FormControl size="small" fullWidth>
                                      <Select
                                        value={parsedData.ADMIXTURE1_SOURCE || ''}
                                        onChange={(e) => {
                                          const updatedData = { ...parsedData, ADMIXTURE1_SOURCE: e.target.value, ADMIXTURE1_SOURCE_TEXT: getAdmixtureSourceFullName(e.target.value, admixtureSources) };
                                          const xmlString = generateXMLFromData(updatedData);
                                          setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                        }}
                                      >
                                        {admixtureSources.map((admixture) => (
                                          <MenuItem key={admixture.id} value={admixture.LIST_KEY}>
                                            {`${admixture.LIST_KEY} - ${admixture.LIST_DESCRIPTION}` || admixture.LIST_KEY}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </TableCell>
                                </TableRow>

                                {/* 外加劑 2 */}
                                <TableRow>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                          <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                            Admixture 2:
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={8}>
                                          <TextField
                                            size="small"
                                            value={parsedData.ADMIXTURE2_CONTENT || ''}
                                            onChange={(e) => {
                                              const updatedData = { ...parsedData, ADMIXTURE2_CONTENT: e.target.value };
                                              const xmlString = generateXMLFromData(updatedData);
                                              setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                            }}
                                            InputProps={{
                                              endAdornment: parsedData.ADMIXTURE_UNIT === 'L_P_M3' ? 'l/m³' : 'kg/m³'
                                            }}
                                            sx={{ width: '100%' }}
                                          />
                                        </Grid>
                                      </Grid>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <TextField
                                      size="small"
                                      value={parsedData.ADMIXTURE2_TYPE || ''}
                                      onChange={(e) => {
                                        const updatedData = { ...parsedData, ADMIXTURE2_TYPE: e.target.value };
                                        const xmlString = generateXMLFromData(updatedData);
                                        setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                      }}
                                      sx={{ width: '100%' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <FormControl size="small" fullWidth>
                                      <Select
                                        value={parsedData.ADMIXTURE2_SOURCE || ''}
                                        onChange={(e) => {
                                          const updatedData = { ...parsedData, ADMIXTURE2_SOURCE: e.target.value , ADMIXTURE2_SOURCE_TEXT: getAdmixtureSourceFullName(e.target.value, admixtureSources) };
                                          const xmlString = generateXMLFromData(updatedData);
                                          setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                        }}
                                      >
                                        {admixtureSources.map((admixture) => (
                                          <MenuItem key={admixture.id} value={admixture.LIST_KEY}>
                                            {`${admixture.LIST_KEY} - ${admixture.LIST_DESCRIPTION}` || admixture.LIST_KEY}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </TableCell>
                                </TableRow>

                                {/* 外加劑 3 */}
                                <TableRow>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                          <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                            Admixture 3:
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={8}>
                                          <TextField
                                            size="small"
                                            value={parsedData.ADMIXTURE3_CONTENT || ''}
                                            onChange={(e) => {
                                              const updatedData = { ...parsedData, ADMIXTURE3_CONTENT: e.target.value };
                                              const xmlString = generateXMLFromData(updatedData);
                                              setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                            }}
                                            InputProps={{
                                              endAdornment: parsedData.ADMIXTURE_UNIT === 'L_P_M3' ? 'l/m³' : 'kg/m³'
                                            }}
                                            sx={{ width: '100%' }}
                                          />
                                        </Grid>
                                      </Grid>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <TextField
                                      size="small"
                                      value={parsedData.ADMIXTURE3_TYPE || ''}
                                      onChange={(e) => {
                                        const updatedData = { ...parsedData, ADMIXTURE3_TYPE: e.target.value };
                                        const xmlString = generateXMLFromData(updatedData);
                                        setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                      }}
                                      sx={{ width: '100%' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <FormControl size="small" fullWidth>
                                      <Select
                                        value={parsedData.ADMIXTURE3_SOURCE || ''}
                                        onChange={(e) => {
                                          const updatedData = { ...parsedData, ADMIXTURE3_SOURCE: e.target.value, ADMIXTURE3_SOURCE_TEXT: getAdmixtureSourceFullName(e.target.value, admixtureSources) };
                                          const xmlString = generateXMLFromData(updatedData);
                                          setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                        }}
                                      >
                                        {admixtureSources.map((admixture) => (
                                          <MenuItem key={admixture.id} value={admixture.LIST_KEY}>
                                            {`${admixture.LIST_KEY} - ${admixture.LIST_DESCRIPTION}` || admixture.LIST_KEY}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </TableCell>
                                </TableRow>

                                {/* 外加劑 4 */}
                                <TableRow>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                          <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                            Admixture 4:
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={8}>
                                          <TextField
                                            size="small"
                                            value={parsedData.ADMIXTURE4_CONTENT || ''}
                                            onChange={(e) => {
                                              const updatedData = { ...parsedData, ADMIXTURE4_CONTENT: e.target.value };
                                              const xmlString = generateXMLFromData(updatedData);
                                              setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                            }}
                                            InputProps={{
                                              endAdornment: parsedData.ADMIXTURE_UNIT === 'L_P_M3' ? 'l/m³' : 'kg/m³'
                                            }}
                                            sx={{ width: '100%' }}
                                          />
                                        </Grid>
                                      </Grid>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <TextField
                                      size="small"
                                      value={parsedData.ADMIXTURE4_TYPE || ''}
                                      onChange={(e) => {
                                        const updatedData = { ...parsedData, ADMIXTURE4_TYPE: e.target.value };
                                        const xmlString = generateXMLFromData(updatedData);
                                        setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                      }}
                                      sx={{ width: '100%' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <FormControl size="small" fullWidth>
                                      <Select
                                        value={parsedData.ADMIXTURE4_SOURCE || ''}
                                        onChange={(e) => {
                                          const updatedData = { ...parsedData, ADMIXTURE4_SOURCE: e.target.value, ADMIXTURE4_SOURCE_TEXT: getAdmixtureSourceFullName(e.target.value, admixtureSources) };
                                          const xmlString = generateXMLFromData(updatedData);
                                          setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                        }}
                                      >
                                        {admixtureSources.map((admixture) => (
                                          <MenuItem key={admixture.id} value={admixture.LIST_KEY}>
                                            {`${admixture.LIST_KEY} - ${admixture.LIST_DESCRIPTION}` || admixture.LIST_KEY}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </TableCell>
                                </TableRow>

                                {/* 外加劑 5 */}
                                <TableRow>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                          <Typography variant="body1" sx={{ mt:1, fontWeight: 'medium', fontSize: '1rem' }}>
                                            Admixture 5:
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={8}>
                                          <TextField
                                            size="small"
                                            value={parsedData.ADMIXTURE5_CONTENT || ''}
                                            onChange={(e) => {
                                              const updatedData = { ...parsedData, ADMIXTURE5_CONTENT: e.target.value };
                                              const xmlString = generateXMLFromData(updatedData);
                                              setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                            }}
                                            InputProps={{
                                              endAdornment: parsedData.ADMIXTURE_UNIT === 'L_P_M3' ? 'l/m³' : 'kg/m³'
                                            }}
                                            sx={{ width: '100%' }}
                                          />
                                        </Grid>
                                      </Grid>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <TextField
                                      size="small"
                                      value={parsedData.ADMIXTURE5_TYPE || ''}
                                      onChange={(e) => {
                                        const updatedData = { ...parsedData, ADMIXTURE5_TYPE: e.target.value };
                                        const xmlString = generateXMLFromData(updatedData);
                                        setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                      }}
                                      sx={{ width: '100%' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                    <FormControl size="small" fullWidth>
                                      <Select
                                        value={parsedData.ADMIXTURE5_SOURCE || ''}
                                        onChange={(e) => {
                                          const updatedData = { ...parsedData, ADMIXTURE5_SOURCE: e.target.value, ADMIXTURE5_SOURCE_TEXT: getAdmixtureSourceFullName(e.target.value, admixtureSources) };
                                          const xmlString = generateXMLFromData(updatedData);
                                          setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                        }}
                                      >
                                        {admixtureSources.map((admixture) => (
                                          <MenuItem key={admixture.id} value={admixture.LIST_KEY}>
                                            {`${admixture.LIST_KEY} - ${admixture.LIST_DESCRIPTION}` || admixture.LIST_KEY}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Paper>

                        {/* 比例和水 */}
                        <Paper sx={{ p: 2, mb: 3 }}>
                          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                            {t('pages.mixID.form.ratio')}
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                                % PFA:
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <TextField
                                fullWidth
                                value={parsedData.PFA_PCT || ''}
                                onChange={(e) => {
                                  const updatedData = { ...parsedData, PFA_PCT: e.target.value };
                                  const xmlString = generateXMLFromData(updatedData);
                                  setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                }}
                                variant="outlined"
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                                {t('pages.mixID.form.freshWaterVol')}:
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <TextField
                                fullWidth
                                value={parsedData.FRESH_WATER_VOL || ''}
                                onChange={(e) => {
                                  const updatedData = { ...parsedData, FRESH_WATER_VOL: e.target.value };
                                  const xmlString = generateXMLFromData(updatedData);
                                  setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                }}
                                variant="outlined"
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                                {t('pages.mixID.form.recycWaterVol')}:
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <TextField
                                fullWidth
                                value={parsedData.RECYC_WATER_VOL || ''}
                                onChange={(e) => {
                                  const updatedData = { ...parsedData, RECYC_WATER_VOL: e.target.value };
                                  const xmlString = generateXMLFromData(updatedData);
                                  setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                }}
                                variant="outlined"
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                                {t('pages.mixID.form.acRatio')}:
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <TextField
                                fullWidth
                                value={parsedData.AC_RATIO || ''}
                                onChange={(e) => {
                                  const updatedData = { ...parsedData, AC_RATIO: e.target.value };
                                  const xmlString = generateXMLFromData(updatedData);
                                  setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                }}
                                variant="outlined"
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                                {t('pages.mixID.form.wcRatio')}:
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <TextField
                                fullWidth
                                value={parsedData.WC_RATIO || ''}
                                onChange={(e) => {
                                  const updatedData = { ...parsedData, WC_RATIO: e.target.value };
                                  const xmlString = generateXMLFromData(updatedData);
                                  setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                }}
                                variant="outlined"
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body1" sx={{mt:2, fontWeight: 'medium', fontSize: '1rem' }}>
                                AAR:
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <TextField
                                fullWidth
                                value={parsedData.AAR_VALUE || ''}
                                onChange={(e) => {
                                  const updatedData = { ...parsedData, AAR_VALUE: e.target.value };
                                  const xmlString = generateXMLFromData(updatedData);
                                  setSelectedTask({ ...selectedTask, ARG1_VALUE: xmlString });
                                }}
                                variant="outlined"
                              />
                            </Grid>
                          </Grid>
                        </Paper>
                      </Box>
                    );
                  } else {
                    return (
                      <Box>
                        <Typography variant="h6" gutterBottom>無法解析XML數據</Typography>
                        <Typography variant="body2">
                          原始XML數據: {selectedTask.ARG1_VALUE}
                        </Typography>
                      </Box>
                    );
                  }
                })()}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setOpenTaskDialog(false)}>取消</Button>
            <Button
              onClick={handleSaveTask}
              variant="contained"
              sx={{ minWidth: 100 }}
            >
              保存
            </Button>
          </DialogActions>
        </Dialog>

        {/* 詳細信息對話框 */}
        <Dialog
          open={openDetailsDialog}
          onClose={() => setOpenDetailsDialog(false)}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: { minHeight: '80vh' }
          }}
        >
          <DialogTitle sx={{ pb: 2 }}>
            <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
              {t('pages.mixID.taskDetails')} - {selectedTaskDetails?.TASK_ID}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ px: 3 }}>
            {selectedTaskDetails && (
              <Box sx={{ mt: 2 }}>
                {/* 詳細變更信息區域 */}
                {selectedTaskDetails.ARG1_VALUE && (
                  <Box sx={{ mt: 2 }}>
                    {(() => {
                      const parsedData = parseXMLData(selectedTaskDetails.ARG1_VALUE);

                      if (parsedData) {
                        return (
                          <Box>
                            {/* 頂部區域 - 供應商和廠房 */}
                            <Paper sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    label={t('pages.mixID.form.concreteSupplier')}
                                    value={getConcreteSupplierFullName(parsedData.CONCRETE_SUPPLIER || '', concreteSuppliers)}
                                    InputProps={{ readOnly: true }}
                                    variant="outlined"
                                  />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    label={t('pages.mixID.form.mixingPlantLocation')}
                                    value={getMixingPlantLocationFullName(parsedData.MIXING_PLANT_LOCATION || '', mixingPlantLocations)}
                                    InputProps={{ readOnly: true }}
                                    variant="outlined"
                                  />
                                </Grid>
                              </Grid>
                            </Paper>

                            {/* Mix ID 基本資訊 */}
                            <Paper sx={{ p: 2, mb: 3 }}>
                              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                                {t('pages.mixID.mixIDInfo')}
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    fullWidth
                                    label="Mix ID"
                                    value={parsedData.MIX_ID || ''}
                                    InputProps={{ readOnly: true }}
                                    variant="outlined"
                                  />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    fullWidth
                                    label="CEDD Mix ID"
                                    value={parsedData.CEDD_MIX_ID || ''}
                                    InputProps={{ readOnly: true }}
                                    variant="outlined"
                                  />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    fullWidth
                                    label="Grade"
                                    value={parsedData.GRADE_TEXT || ''}
                                    InputProps={{ readOnly: true }}
                                    variant="outlined"
                                  />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <TextField
                                    fullWidth
                                    label="Slump"
                                    value={parsedData.DESIGNED_SLUMP || ''}
                                    InputProps={{ readOnly: true }}
                                    variant="outlined"
                                  />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <TextField
                                    fullWidth
                                    label="Flow Value"
                                    value={parsedData.DESIGNED_FLOW_VALUE || ''}
                                    InputProps={{ readOnly: true }}
                                    variant="outlined"
                                  />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <TextField
                                    fullWidth
                                    label="Slump Flow"
                                    value={parsedData.DESIGNED_SLUMP_FLOW || ''}
                                    InputProps={{ readOnly: true }}
                                    variant="outlined"
                                  />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <TextField
                                    fullWidth
                                    label="Usage"
                                    value={parsedData.USAGE || ''}
                                    InputProps={{ readOnly: true }}
                                    variant="outlined"
                                  />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    label="T500 (sec)"
                                    value={parsedData.T_500 || ''}
                                    InputProps={{ readOnly: true }}
                                    variant="outlined"
                                  />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    label="TEnd-of-flow (sec)"
                                    value={parsedData.T_END_OF_FLOW || ''}
                                    InputProps={{ readOnly: true }}
                                    variant="outlined"
                                  />
                                </Grid>
                              </Grid>
                            </Paper>

                            {/* 配比內容表格 */}
                            <Paper sx={{ p: 2, mb: 3 }}>
                              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                                {t('pages.mixID.form.mixContent')}
                              </Typography>
                              <TableContainer>
                                <Table size="small" sx={{ tableLayout: 'fixed' }}>
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: 'primary.light' }}>
                                      <TableCell sx={{ fontWeight: 'bold', width: '35%', textAlign: 'left' }}>{t('pages.mixID.form.mixContent')}</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '32%', textAlign: 'center' }}>{t('pages.mixID.form.type')}</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '33%', textAlign: 'center' }}>{t('pages.mixID.form.source')}</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {/* 水泥 */}
                                    <TableRow>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
                                            Cement:
                                          </Typography>
                                          <TextField
                                            size="small"
                                            value={parsedData.CEMENT_CONTENT || ''}
                                            InputProps={{
                                              readOnly: true,
                                              endAdornment: 'kg/m³'
                                            }}
                                            sx={{ width: '100%' }}
                                          />
                                        </Box>
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={parsedData.CEMENT_TYPE ? parsedData.CEMENT_TYPE + ' - ' + parsedData.CEMENT_TYPE_TEXT : ''}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={parsedData.CEMENT_SOURCE ? parsedData.CEMENT_SOURCE + ' - ' + parsedData.CEMENT_SOURCE_TEXT :  ''}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                    </TableRow>

                                    {/* PFA */}
                                    <TableRow>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
                                            PFA:
                                          </Typography>
                                          <TextField
                                            size="small"
                                            value={parsedData.PFA_CONTENT || ''}
                                            InputProps={{
                                              readOnly: true,
                                              endAdornment: 'kg/m³'
                                            }}
                                            sx={{ width: '100%' }}
                                          />
                                        </Box>
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={parsedData.PFA_TYPE || ''}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={parsedData.PFA_SOURCE ? parsedData.PFA_SOURCE + ' - ' + parsedData.PFA_SOURCE_TEXT : ''}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                    </TableRow>

                                    {/* GGBS */}
                                    <TableRow>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
                                            GGBS:
                                          </Typography>
                                          <TextField
                                            size="small"
                                            value={parsedData.GGBS_CONTENT || ''}
                                            InputProps={{
                                              readOnly: true,
                                              endAdornment: 'kg/m³'
                                            }}
                                            sx={{ width: '100%' }}
                                          />
                                        </Box>
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={parsedData.GGBS_TYPE || ''}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={parsedData.GGBS_SOURCE ? parsedData.GGBS_SOURCE + ' - ' + parsedData.GGBS_SOURCE_TEXT : ''}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                    </TableRow>

                                    {/* 骨料 (40mm) */}
                                    <TableRow>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
                                            Aggregate (40mm):
                                          </Typography>
                                          <TextField
                                            size="small"
                                            value={parsedData.AGG_40_CONTENT || ''}
                                            InputProps={{
                                              readOnly: true,
                                              endAdornment: 'kg/m³'
                                            }}
                                            sx={{ width: '100%' }}
                                          />
                                        </Box>
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={parsedData.AGG_40_TYPE || ''}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={parsedData.AGG_40_SOURCE ? parsedData.AGG_40_SOURCE + ' - ' + parsedData.AGG_40_SOURCE_TEXT : ''}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                    </TableRow>

                                    {/* 骨料 (20mm) */}
                                    <TableRow>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
                                            Aggregate (20mm):
                                          </Typography>
                                          <TextField
                                            size="small"
                                            value={parsedData.AGG_20_CONTENT || ''}
                                            InputProps={{
                                              readOnly: true,
                                              endAdornment: 'kg/m³'
                                            }}
                                            sx={{ width: '100%' }}
                                          />
                                        </Box>
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={parsedData.AGG_20_TYPE || ''}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={parsedData.AGG_20_SOURCE ? parsedData.AGG_20_SOURCE + ' - ' + parsedData.AGG_20_SOURCE_TEXT : ''}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                    </TableRow>

                                    {/* 骨料 (10mm) */}
                                    <TableRow>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
                                            Aggregate (10mm):
                                          </Typography>
                                          <TextField
                                            size="small"
                                            value={parsedData.AGG_10_CONTENT || ''}
                                            InputProps={{
                                              readOnly: true,
                                              endAdornment: 'kg/m³'
                                            }}
                                            sx={{ width: '100%' }}
                                          />
                                        </Box>
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={parsedData.AGG_10_TYPE || ''}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={parsedData.AGG_10_SOURCE ? parsedData.AGG_10_SOURCE + ' - ' + parsedData.AGG_10_SOURCE_TEXT : ''}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                    </TableRow>

                                    {/* 細骨料 */}
                                    <TableRow>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
                                            Fine Aggregate:
                                          </Typography>
                                          <TextField
                                            size="small"
                                            value={parsedData.AGG_FINE_CONTENT || ''}
                                            InputProps={{
                                              readOnly: true,
                                              endAdornment: 'kg/m³'
                                            }}
                                            sx={{ width: '100%' }}
                                          />
                                        </Box>
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={parsedData.AGG_FINE_TYPE || ''}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={parsedData.AGG_FINE_SOURCE ? parsedData.AGG_FINE_SOURCE + ' - ' + parsedData.AGG_FINE_SOURCE_TEXT : ''}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                    </TableRow>

                                    {/* 外加劑 1 */}
                                    <TableRow>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
                                            Admixture 1:
                                          </Typography>
                                          <TextField
                                            size="small"
                                            value={parsedData.ADMIXTURE1_CONTENT || ''}
                                            InputProps={{
                                              readOnly: true,
                                              endAdornment: parsedData.ADMIXTURE_UNIT === 'L_P_M3' ? 'l/m³' : 'kg/m³'
                                            }}
                                            sx={{ width: '100%' }}
                                          />
                                        </Box>
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={parsedData.ADMIXTURE1_TYPE || ''}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={(parsedData.ADMIXTURE1_SOURCE || '') + (parsedData.ADMIXTURE1_SOURCE_TEXT ? ' - ' + parsedData.ADMIXTURE1_SOURCE_TEXT : '')}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                    </TableRow>

                                    {/* 外加劑 2 */}
                                    <TableRow>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
                                            Admixture 2:
                                          </Typography>
                                          <TextField
                                            size="small"
                                            value={parsedData.ADMIXTURE2_CONTENT || ''}
                                            InputProps={{
                                              readOnly: true,
                                              endAdornment: parsedData.ADMIXTURE_UNIT === 'L_P_M3' ? 'l/m³' : 'kg/m³'
                                            }}
                                            sx={{ width: '100%' }}
                                          />
                                        </Box>
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={parsedData.ADMIXTURE2_TYPE || ''}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={(parsedData.ADMIXTURE2_SOURCE || '') + (parsedData.ADMIXTURE2_SOURCE_TEXT ? ' - ' + parsedData.ADMIXTURE2_SOURCE_TEXT : '')}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                    </TableRow>

                                    {/* 外加劑 3 */}
                                    <TableRow>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
                                            Admixture 3:
                                          </Typography>
                                          <TextField
                                            size="small"
                                            value={parsedData.ADMIXTURE3_CONTENT || ''}
                                            InputProps={{
                                              readOnly: true,
                                              endAdornment: parsedData.ADMIXTURE_UNIT === 'L_P_M3' ? 'l/m³' : 'kg/m³'
                                            }}
                                            sx={{ width: '100%' }}
                                          />
                                        </Box>
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={parsedData.ADMIXTURE3_TYPE || ''}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={(parsedData.ADMIXTURE3_SOURCE || '') + (parsedData.ADMIXTURE3_SOURCE_TEXT ? ' - ' + parsedData.ADMIXTURE3_SOURCE_TEXT : '')}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                    </TableRow>

                                    {/* 外加劑 4 */}
                                    <TableRow>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
                                            Admixture 4:
                                          </Typography>
                                          <TextField
                                            size="small"
                                            value={parsedData.ADMIXTURE4_CONTENT || ''}
                                            InputProps={{
                                              readOnly: true,
                                              endAdornment: parsedData.ADMIXTURE_UNIT === 'L_P_M3' ? 'l/m³' : 'kg/m³'
                                            }}
                                            sx={{ width: '100%' }}
                                          />
                                        </Box>
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={parsedData.ADMIXTURE4_TYPE || ''}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={(parsedData.ADMIXTURE4_SOURCE || '') + (parsedData.ADMIXTURE4_SOURCE_TEXT ? ' - ' + parsedData.ADMIXTURE4_SOURCE_TEXT : '')}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                    </TableRow>

                                    {/* 外加劑 5 */}
                                    <TableRow>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
                                            Admixture 5:
                                          </Typography>
                                          <TextField
                                            size="small"
                                            value={parsedData.ADMIXTURE5_CONTENT || ''}
                                            InputProps={{
                                              readOnly: true,
                                              endAdornment: parsedData.ADMIXTURE_UNIT === 'L_P_M3' ? 'l/m³' : 'kg/m³'
                                            }}
                                            sx={{ width: '100%' }}
                                          />
                                        </Box>
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={parsedData.ADMIXTURE5_TYPE || ''}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'middle', py: 1.5, px: 2 }}>
                                        <TextField
                                          size="small"
                                          value={(parsedData.ADMIXTURE5_SOURCE || '') + (parsedData.ADMIXTURE5_SOURCE_TEXT ? ' - ' + parsedData.ADMIXTURE5_SOURCE_TEXT : '')}
                                          InputProps={{ readOnly: true }}
                                          sx={{ width: '100%' }}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Paper>

                            {/* 比例和水 */}
                            <Paper sx={{ p: 2, mb: 3 }}>
                              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                                {t('pages.mixID.form.ratio')}
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={3}>
                                  <TextField
                                    fullWidth
                                    label="% PFA"
                                    value={parsedData.PFA_PCT || ''}
                                    InputProps={{ readOnly: true }}
                                    variant="outlined"
                                  />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <TextField
                                    fullWidth
                                    label={t('pages.mixID.form.freshWater')}
                                    value={parsedData.FRESH_WATER_VOL || ''}
                                    InputProps={{ readOnly: true }}
                                    variant="outlined"
                                  />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <TextField
                                    fullWidth
                                    label={t('pages.mixID.form.recycWater')}
                                    value={parsedData.RECYC_WATER_VOL || ''}
                                    InputProps={{ readOnly: true }}
                                    variant="outlined"
                                  />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <TextField
                                    fullWidth
                                    label={t('pages.mixID.form.acRatio')}
                                    value={parsedData.AC_RATIO || ''}
                                    InputProps={{ readOnly: true }}
                                    variant="outlined"
                                  />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <TextField
                                    fullWidth
                                    label={t('pages.mixID.form.wcRatio')}
                                    value={parsedData.WC_RATIO || ''}
                                    InputProps={{ readOnly: true }}
                                    variant="outlined"
                                  />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <TextField
                                    fullWidth
                                    label="AAR"
                                    value={parsedData.AAR_VALUE || ''}
                                    InputProps={{ readOnly: true }}
                                    variant="outlined"
                                  />
                                </Grid>
                              </Grid>
                            </Paper>
                          </Box>
                        );
                      } else {
                        // 如果解析失敗，顯示原始XML
                        return (
                          <Paper sx={{ p: 2, mb: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                              詳細變更信息 (原始XML)
                            </Typography>
                            <Box
                              sx={{
                                backgroundColor: '#f8f9fa',
                                p: 2,
                                borderRadius: 1,
                                border: '1px solid #dee2e6',
                                maxHeight: '400px',
                                overflow: 'auto',
                                fontFamily: 'monospace'
                              }}
                            >
                              <Typography
                                variant="body2"
                                component="pre"
                                sx={{
                                  whiteSpace: 'pre-wrap',
                                  fontFamily: 'monospace',
                                  fontSize: '0.875rem',
                                  margin: 0
                                }}
                              >
                                {selectedTaskDetails.ARG1_VALUE}
                              </Typography>
                            </Box>
                          </Paper>
                        );
                      }
                    })()}
                  </Box>
                )}

                {/* 其他參數區域 */}
                {(selectedTaskDetails.ARG2_VALUE || selectedTaskDetails.ARG3_VALUE || selectedTaskDetails.ARG4_VALUE || selectedTaskDetails.ARG5_VALUE) && (
                  <Paper sx={{ p: 2, mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                      其他參數
                    </Typography>
                    <Grid container spacing={2}>
                      {selectedTaskDetails.ARG2_VALUE && (
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="ARG2_VALUE"
                            value={selectedTaskDetails.ARG2_VALUE}
                            InputProps={{ readOnly: true }}
                            variant="outlined"
                            multiline
                            rows={2}
                          />
                        </Grid>
                      )}
                      {selectedTaskDetails.ARG3_VALUE && (
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="ARG3_VALUE"
                            value={selectedTaskDetails.ARG3_VALUE}
                            InputProps={{ readOnly: true }}
                            variant="outlined"
                            multiline
                            rows={2}
                          />
                        </Grid>
                      )}
                      {selectedTaskDetails.ARG4_VALUE && (
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="ARG4_VALUE"
                            value={selectedTaskDetails.ARG4_VALUE}
                            InputProps={{ readOnly: true }}
                            variant="outlined"
                            multiline
                            rows={2}
                          />
                        </Grid>
                      )}
                      {selectedTaskDetails.ARG5_VALUE && (
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="ARG5_VALUE"
                            value={selectedTaskDetails.ARG5_VALUE}
                            InputProps={{ readOnly: true }}
                            variant="outlined"
                            multiline
                            rows={2}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                )}

                {/* 返回值區域 */}
                {selectedTaskDetails.RETURN_VALUE && (
                  <Paper sx={{ p: 2, mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                      返回值
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="返回值"
                          value={selectedTaskDetails.RETURN_VALUE}
                          InputProps={{ readOnly: true }}
                          variant="outlined"
                          multiline
                          rows={3}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setOpenDetailsDialog(false)}
              variant="outlined"
              sx={{ minWidth: 100 }}
            >
              {t('common.close')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 確認對話框 */}
        <Dialog
          open={confirmDialog.open}
          onClose={() => setConfirmDialog({ open: false, title: '', message: '', action: '', task: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ pb: 2 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
              {confirmDialog.title}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ px: 3 }}>
            <Typography variant="body1" sx={{ mt: 1 }}>
              {confirmDialog.message}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setConfirmDialog({ open: false, title: '', message: '', action: '', task: null })}
              variant="outlined"
              sx={{ minWidth: 100 }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={confirmDialog.action === 'approve' ? handleConfirmApprove : handleConfirmReject}
              variant="contained"
              color={confirmDialog.action === 'approve' ? 'success' : 'error'}
              sx={{ minWidth: 100 }}
            >
              {confirmDialog.action === 'approve' ? t('common.approve') : t('common.reject')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 通知 */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </PageLayout>
  );
};

export default TaskPage;